import React, {
  type ChangeEvent,
  type FC,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaAngleUp, FaPlus } from "react-icons/fa";
import { FaLocationCrosshairs } from "react-icons/fa6";
import { IoIosSearch } from "react-icons/io";
import { IoCloseCircle } from "react-icons/io5";
import { Link, useI18next } from "gatsby-plugin-react-i18next";

import LandingPricing from "@data/landing-pricing.json";
import Regions from "@data/regions.json";
import type Country from "@interfaces/Country";
import { type default as LandingPricingType } from "@interfaces/LandingPricing";
import type Region from "@interfaces/Region";
import { useConfigurationState } from "@stores/configuration";
import {
  cn,
  defaultRipple,
  getRealLocalCountryCode,
  normalize,
  primaryRipple,
  range,
} from "@utilities/helper";
import { AnimatePresence, motion } from "framer-motion";

type CountryType = Record<string, string>;

const Countries = (LandingPricing as LandingPricingType).countries;
const countriesKeys = Object.keys(Countries);
const countriesValues = Object.values(Countries);

const loadLocations = (
  language: string = "en",
): { countries: Country[]; regions: Region[] } => {
  const localCountryCode = getRealLocalCountryCode();

  const loadedCountries: Country[] = [];
  const loadedRegions: Region[] = [];

  let initialDisplayedCountriesCount: number = 0;
  countriesKeys.forEach((id, i) => {
    const content = countriesValues[i][language] || countriesValues[i].en;
    const isLocal = localCountryCode?.toLowerCase() === id.toLowerCase();
    const shouldShowInitial = isLocal || !!content.popularity_position;
    if (shouldShowInitial) {
      initialDisplayedCountriesCount++;
    }
    loadedCountries.push({
      id,
      content,
      is_local: isLocal,
      should_show_initial: shouldShowInitial,
    });
  });
  loadedCountries.sort((a, b) => {
    // First priority: is_local (true values come first)
    const isLocalA = a.is_local ? 1 : 0;
    const isLocalB = b.is_local ? 1 : 0;

    if (isLocalA > isLocalB) return -1;
    if (isLocalA < isLocalB) return 1;

    // Second priority: popularity_position (treating null as the lowest value)
    const popA = parseInt(a.content.popularity_position ?? "99");
    const popB = parseInt(b.content.popularity_position ?? "99");

    if (popA - popB < 0) return -1;
    if (popA - popB > 0) return 1;

    // Third priority: country_name
    const countryA = a.content.country_name ?? "";
    const countryB = b.content.country_name ?? "";

    if (countryA < countryB) return -1;
    if (countryA > countryB) return 1;

    // If all criteria are the same, return 0
    return 0;
  });
  if (initialDisplayedCountriesCount % 2 !== 0) {
    const countryToUpdate = loadedCountries.find(
      country => !country.should_show_initial,
    );
    if (countryToUpdate) {
      countryToUpdate.should_show_initial = true;
    }
  }

  Regions.forEach((region: Region) => {
    loadedRegions.push({
      ...region,
      is_local: region.coverages.includes(localCountryCode),
    });
  });

  return {
    countries: loadedCountries,
    regions: loadedRegions,
  };
};

const LocationItem: FC<{
  isRegional: boolean;
  isFromLoading: boolean;
  isLocal: boolean;
  imgSrc: string;
  name: string;
  url: string;
}> = ({ isRegional, isFromLoading, isLocal, imgSrc, name, url }) => {
  return (
    <motion.div
      className="relative"
      initial={{
        opacity: 0,
        height: isFromLoading ? "auto" : 0,
        scale: isFromLoading ? 1 : 0.75,
      }}
      animate={{ opacity: 1, height: "auto", scale: 1 }}
      exit={{
        opacity: 0,
        height: isFromLoading ? "auto" : 0,
        scale: isFromLoading ? 1 : 0.75,
      }}
      transition={{ duration: 0.3 }}
    >
      <Link
        ref={primaryRipple}
        className="focus:custom-primary-focus-shadow flex h-full flex-col items-center justify-center rounded-xl border border-solid border-gray-300 bg-white p-7 text-center text-base text-black no-underline shadow-lg transition duration-300 hover:bg-primary-100/60 hover:no-underline sm:text-lg md:text-xl"
        to={url}
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        {isRegional ? (
          <img
            className="mx-auto mb-3 block"
            src={imgSrc}
            alt={name}
            loading="lazy"
            width={60}
            height={60}
          />
        ) : (
          <div className="mx-auto mb-3 overflow-hidden rounded-full border border-transparent shadow-md">
            <img
              className="aspect-square object-cover"
              width={58}
              height={58}
              src={imgSrc}
              alt={name}
            />
          </div>
        )}
        <span>{name}</span>
        {isLocal && (
          <FaLocationCrosshairs className="absolute left-4 top-4 size-6 text-green-600" />
        )}
      </Link>
    </motion.div>
  );
};

const LocationList: FC<{
  title: string | ReactNode;
  animationX?: string;
  children: ReactNode;
}> = ({ title, animationX, children }) => {
  return (
    <motion.div
      initial={{
        opacity: 0,
        x: animationX,
        position: "absolute",
      }}
      animate={{ opacity: 1, x: "0%", position: "relative" }}
      exit={{
        opacity: 0,
        x: animationX,
        position: "absolute",
      }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="mb-4 text-center text-xl md:text-2xl lg:text-3xl">
        {title}
      </h2>
      <div className="grid grid-cols-2 flex-wrap justify-center gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {children}
      </div>
    </motion.div>
  );
};

const GeoLocationList = (): ReactElement => {
  const { t, language } = useI18next();

  const [countries, setCountries] = useState<Country[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  const configurationState = useConfigurationState();
  const [selectedTab, setSelectedTab] = useState(
    configurationState.lastVisitedLocationTab,
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFromLoading, setIsFromLoading] = useState<boolean>(true); // for animation

  useEffect(() => {
    const loadedLocations = loadLocations(language);
    setCountries(loadedLocations.countries);
    setRegions(loadedLocations.regions);
  }, [language]);
  useEffect(() => {
    configurationState.updateLastVisitedLocationTab(selectedTab);
  }, [selectedTab]);

  const [allCountryListOpen, setAllCountryListOpen] = useState<boolean>(false);

  const [currentState, setCurrentState] = useState<{
    query: string;
    countryList: Country[];
    regionList: Region[];
  }>({
    query: "",
    countryList: [],
    regionList: [],
  });

  const filterInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    resetState();
    filterInput.current?.focus();

    return () => {
      setIsLoading(false);
      setTimeout(() => {
        setIsFromLoading(false);
      }, 1500);
    };
  }, [countries, regions]);

  const filterLocations = (event: ChangeEvent<HTMLInputElement>): void => {
    const filteredQuery = event.target.value;
    setAllCountryListOpen(!!filteredQuery);
    if (filteredQuery) {
      const normalizedQuery = normalize(filteredQuery);
      const filteredCountryResults: Country[] = countries.filter(country => {
        return (
          normalize(country.id).includes(normalizedQuery) ||
          normalize(country.content.country_name).includes(normalizedQuery)
        );
      });
      const filteredRegionResults: Region[] = regions.filter(region => {
        return (
          normalize(region.name[language]).includes(normalizedQuery) ||
          normalize(region.slug).includes(normalizedQuery)
        );
      });

      if (
        selectedTab === 1 &&
        filteredCountryResults.length === 0 &&
        filteredRegionResults.length > 0
      ) {
        setSelectedTab(2);
      } else if (
        selectedTab === 2 &&
        filteredRegionResults.length === 0 &&
        filteredCountryResults.length > 0
      ) {
        setSelectedTab(1);
      }

      setCurrentState({
        query: filteredQuery,
        countryList: filteredCountryResults,
        regionList: filteredRegionResults,
      });
    } else {
      resetState();
    }
  };
  const resetState = (): void => {
    setCurrentState({
      query: "",
      countryList: countries,
      regionList: regions,
    });
    setAllCountryListOpen(false);
  };
  const updateTab = (value: number): void => {
    if (typeof window !== "undefined" && window.scrollTo) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
    setSelectedTab(value);
  };

  const toggleCountryList = (): void => {
    if (allCountryListOpen) {
      if (typeof window !== "undefined" && window.scrollTo) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
    setAllCountryListOpen(!allCountryListOpen);
  };

  return (
    <section className="relative pb-2">
      <div className="container sticky top-[75px] z-[1] mx-auto w-full bg-neutral-100 pb-4 pt-2">
        {/* counties-regions filter */}
        <div className="relative">
          <label>
            <IoIosSearch className="absolute left-[14px] top-[14px] z-[1] size-[22px] text-gray-400" />
            <input
              ref={filterInput}
              placeholder={t("where_do_you_need_the_data") ?? ""}
              autoComplete="on"
              autoCorrect="on"
              className="custom-form-control block w-full !py-3 !pl-12 !pr-5"
              type="text"
              aria-label="Search"
              value={currentState.query}
              onChange={event => {
                filterLocations(event);
              }}
            ></input>
            <AnimatePresence>
              {!!currentState.query && (
                <motion.button
                  ref={primaryRipple}
                  onClick={resetState}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="focus:custom-primary-focus-ring group absolute right-[14px] top-[14px] z-[1] flex items-center justify-center rounded-full !border-0 outline-none transition duration-300 hover:!opacity-60"
                >
                  <IoCloseCircle className="size-[22px] text-gray-400 transition duration-300 group-hover:text-primary-400" />
                </motion.button>
              )}
            </AnimatePresence>
          </label>
        </div>

        {/* counties-regions switcher */}
        <div className="mt-4 rounded-2xl bg-gray-50 p-2">
          <div className="relative">
            <motion.div
              initial={{ left: "0%" }}
              animate={{ left: selectedTab === 1 ? "0%" : "50%" }}
              className="pointer-events-none absolute h-full w-1/2 rounded-xl bg-primary"
            ></motion.div>
            <div className="grid grid-cols-2 justify-center rounded-2xl text-base font-semibold md:text-xl">
              <button
                ref={primaryRipple}
                onClick={() => {
                  updateTab(1);
                }}
                className={cn(
                  "focus:custom-primary-focus-shadow z-10 rounded-xl border !border-transparent p-2 transition duration-300",
                  {
                    "text-white": selectedTab === 1,
                    "hover:text-primary": selectedTab !== 1,
                  },
                )}
              >
                {t("local_plan")}
              </button>
              <button
                ref={primaryRipple}
                onClick={() => {
                  updateTab(2);
                }}
                className={cn(
                  "focus:custom-primary-focus-shadow z-10 rounded-xl border !border-transparent p-2 transition duration-300",
                  {
                    "text-white": selectedTab === 2,
                    "hover:text-primary": selectedTab !== 2,
                  },
                )}
              >
                {t("regional_plan")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        <div className="container relative mb-20">
          {!isLoading ? (
            <>
              {/* countries */}
              {selectedTab === 1 && (
                <LocationList
                  title={
                    currentState.query ? (
                      <span className="text-base">
                        {t("searching_countries_title")}
                      </span>
                    ) : (
                      t("popular_countries")
                    )
                  }
                  animationX={isFromLoading ? "0%" : "-100%"}
                >
                  {currentState.countryList.map(
                    (country: Country, i: number) => {
                      const isShow =
                        country.should_show_initial || allCountryListOpen;

                      return (
                        isShow && (
                          <LocationItem
                            key={"country_" + i}
                            isRegional={false}
                            isFromLoading={isFromLoading}
                            isLocal={!!country.is_local}
                            name={country.content.country_name}
                            imgSrc={
                              "/images/countries/" +
                              country.id.toLowerCase() +
                              ".png"
                            }
                            url={"/plans/" + country.id.toLowerCase()}
                          />
                        )
                      );
                    },
                  )}
                </LocationList>
              )}

              {/* regions */}
              {selectedTab === 2 && (
                <LocationList
                  title={t("regional_esims")}
                  animationX={isFromLoading ? "0%" : "100%"}
                >
                  {currentState.regionList.map((region: Region, i: number) => {
                    return (
                      <LocationItem
                        key={"region_" + i}
                        isRegional={true}
                        isFromLoading={isFromLoading}
                        isLocal={!!region.is_local}
                        name={region.name[language]}
                        imgSrc={region.image}
                        url={"/plans/" + region.slug}
                      />
                    );
                  })}
                </LocationList>
              )}

              {/* show/hide full list */}
              {selectedTab === 1 && !currentState.query && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  ref={defaultRipple}
                  onClick={event => {
                    toggleCountryList();
                    (event.target as HTMLButtonElement).blur();
                  }}
                  className="custom-btn-primary mx-auto mt-20 flex items-center gap-1 !px-10 !py-2 font-semibold text-white"
                >
                  {allCountryListOpen ? (
                    <>
                      <span>{t("see_less")}</span>
                      <FaAngleUp className="text-sm" />
                    </>
                  ) : (
                    <>
                      <span>{t("see_all_countries")}</span>
                      <FaPlus className="text-sm" />
                    </>
                  )}
                </motion.button>
              )}
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0.8 }}
              animate={{ opacity: 1 }}
              exit={{ position: "absolute", opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="top-0 z-10 mx-auto min-h-svh w-full bg-neutral-100"
            >
              <div className="mx-auto mb-4 h-8 w-[200px] animate-pulse rounded-full bg-gray-200"></div>
              <div className="grid grid-cols-2 flex-wrap justify-center gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {range(1, 16).map((value, index) => {
                  return (
                    <div
                      key={"skeleton_key_" + index}
                      className="pointer-events-none flex h-full flex-col items-center justify-center rounded-xl border border-solid border-gray-300 bg-white p-7 shadow-lg transition duration-300"
                    >
                      <div className="mx-auto mb-3 size-[60px] animate-pulse overflow-hidden rounded-full border-2 border-solid border-gray-200 bg-gray-200"></div>
                      <div className="h-6 w-3/4 animate-pulse rounded-full bg-gray-200"></div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>
      </AnimatePresence>
    </section>
  );
};

export default GeoLocationList;
